<template>
  <div class="w-full flex justify-between gap-2">
    <MenuItem as="button" :on-click="redirectToCanva" :disabled="isLoading">
      <template #start>
        <component :is="iconComponent" />
      </template>
      <template #title>
        <span class="text-typo-primary font-semi-bold text-sm truncate">{{ props.label }}</span>
      </template>
      <template #end>
        <Badge tone="secondary" class="flex items-center !p-1">
          <CanvaIcon width="16" height="16" />
          <span class="text-xs ml-1">{{ I18n.t("canva_cta.common.open") }}</span>
        </Badge>
      </template>
    </MenuItem>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, nextTick } from "vue";
import { CanvaIcon, Badge, MenuItem } from "prism";
import { useCanvaLinkBuilder, CanvaVariant, CanvaCTAIdentifier } from "@/composables/canva_link_builder";
import { hasMagicBrushHdResult, Image, ProcessingState, ResultVariant } from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import IconDesign from "@/components/upload/editor_components/side_panel_icons/icon-design.vue";

import { rbgRedirectToCanvaClickedV101, rbgRedirectToCanvaStartedV101 } from "kaleido-event-tracker";
import { I18n as I18nType } from "i18n-js";
import { PersistentStore } from "@/stores/persistent_store";
import { Stage } from "konva/lib/Stage";

export interface CanvaCtaNewProps {
  image: Image;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
  stage: any;
  stageHd: any;
  getDataURL: Function;
  canvaVariant: CanvaVariant;
  canvaCtaIdentifier: CanvaCTAIdentifier;
  label: string;
  icon?: any;
}
const props = withDefaults(defineProps<CanvaCtaNewProps>(), {
  canvaVariant: CanvaVariant.INLINE_EDITOR_DEFAULT,
  icon: IconDesign,
});

const isLoading = ref<boolean>(false);
const variant = ref<ResultVariant>(ResultVariant.Preview);
const preview = ref<string>(props.getDataURL(props.image, props.stage.getStage(), props.stage.getStage()));

const flavor = document.querySelector("meta[property='canva-env']").getAttribute("content");
const { buildUrl } = useCanvaLinkBuilder(flavor);

const I18n: I18nType = inject("I18n");

const iconComponent = computed(() => {
  return props.icon || IconDesign;
});

const refreshPreview = async (): Promise<void> => {
  return new Promise(async (resolve) => {
    const image = props.image;
    const isMagicBrushResult =
      props.persistentStore.previewVariant === ResultVariant.MagicBrushPreview ||
      props.persistentStore.hdVariant === ResultVariant.MagicBrushHd;
    let isHdAvailable = false;

    if (isMagicBrushResult) {
      const iterationId = props.persistentStore.magicBrushCache?.iterationId || image.meta.ai_brush_last_iteration;
      isHdAvailable = hasMagicBrushHdResult(image, iterationId);
      variant.value = isHdAvailable ? ResultVariant.MagicBrushHd : ResultVariant.MagicBrushPreview;
    } else {
      isHdAvailable = image.hdResult?.state === ProcessingState.Finished;
      variant.value = isHdAvailable ? ResultVariant.Hd : ResultVariant.Preview;
    }

    if (isHdAvailable) {
      await props.refreshAllHdLayers(image);
      preview.value = (await props.stageHd()).toDataURL({
        x: 0,
        y: 0,
        width: image.meta.hdWidth,
        height: image.meta.hdHeight,
      });
    } else {
      preview.value = props.getDataURL(image, props.stage.getStage());
    }

    resolve();
  });
};

const redirectToCanva = async () => {
  rbgRedirectToCanvaClickedV101({ image_id: props.image.meta.id, source: props.canvaCtaIdentifier });
  isLoading.value = true;

  await refreshPreview();

  const image = props.image;
  try {
    const result = await Client.uploadToBucket(image, preview.value, variant.value);

    if (result.success) {
      const url = buildUrl(result.url, props.canvaVariant, props.canvaCtaIdentifier);
      // Safari on iOS is blocking all window.open calls inside async functions
      nextTick(() => {
        rbgRedirectToCanvaStartedV101({ image_id: props.image.meta.id, source: props.canvaCtaIdentifier });
        window.open(url, "_blank");
      });
    } else {
      throw new Error("Upload to bucket failed");
    }
  } catch (error) {
    console.error("Error uploading to bucket", error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.truncate {
  max-width: 150px !important;
}
</style>
